/** A deadline releases the caller even when the awaited bridge ignores cancellation.
 * Non-cancellable work must check the signal before publishing its result. */
export async function withAsyncDeadline<T>(run: (signal: AbortSignal) => Promise<T>, ms: number, parent?: AbortSignal): Promise<T> {
    const controller = new AbortController();
    const abort = (): void => controller.abort(parent?.reason);
    let expired!: () => void;
    const cancelled = new Promise<never>((_, reject) => {
        expired = () => reject(controller.signal.reason);
        controller.signal.addEventListener('abort', expired, { once: true });
    });
    const timer = setTimeout(() => controller.abort(new DOMException('Async work timed out', 'TimeoutError')), ms);
    parent?.addEventListener('abort', abort, { once: true });
    if (parent?.aborted) abort();
    try {
        let work: Promise<T>;
        try { controller.signal.throwIfAborted(); work = run(controller.signal); }
        catch (error) { work = Promise.reject(error); }
        return await Promise.race([cancelled, work]);
    } finally {
        clearTimeout(timer);
        parent?.removeEventListener('abort', abort);
        controller.signal.removeEventListener('abort', expired);
    }
}
